import type { ReactNode } from 'react';

import { Icon, type IconName } from '@/components/ui/icon';
import { cn } from '@/lib/cn';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<AlertTone, { className: string; icon: IconName }> = {
  info: { className: 'bg-primary-soft border-primary/25 text-primary', icon: 'info' },
  success: { className: 'bg-success-soft border-success/25 text-success', icon: 'check-circle' },
  warning: { className: 'bg-warning-soft border-warning/25 text-warning', icon: 'alert-circle' },
  danger: { className: 'bg-danger-soft border-danger/25 text-danger', icon: 'alert-circle' },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Rendered at the end of the alert, typically a retry button. */
  action?: ReactNode;
  className?: string;
}

export function Alert({ tone = 'info', title, children, action, className }: AlertProps) {
  const { className: toneClass, icon } = TONES[tone];

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-2 rounded-sm border px-2.5 py-2', toneClass, className)}
    >
      <Icon name={icon} size={15} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        {title && <p className="text-xs font-bold">{title}</p>}
        {children && (
          <div className={cn('text-xs text-content-muted', title && 'mt-0.5')}>{children}</div>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
